import { LINK_TARGET } from '@thoughtspot/browser-util';
import { extractCaptionTitleAndLink } from '@thoughtspot/formatter-service';
import { translate } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { AlertLink } from '@thoughtspot/radiant-react/widgets/alert';
import { SafeHTML } from '@thoughtspot/radiant-react/widgets/safe-html';
import * as cheerio from 'cheerio';
import _ from 'lodash';
import React from 'react';
import {
    ColumnTimeBucket,
    ColumnValueType,
    Filter,
    FilterColumnValueInput,
    FilterContent,
    FilterContentInput,
    FilterGroup,
    FilterTypes,
} from '/@services/generated/graphql-types';
import {
    getTextFromHTMLTags,
    hasHtmlTags,
} from '../../../services/answer/answer.util';
import {
    alphaNumericCompareFn,
    AttributeFilter,
    getAttributeFilterValue,
    isFilterNonEditable,
} from '../answer-filter.util';
import { NUMERICAL_DATE_BUCKETS } from '../date-range-filter/date-range-filter.util';
import styles from './attribute-filter.module.scss';

const logger = create('attribute-filter');
/**
 * Assumption here is that for attributes there could be at most
 * two filters applied. One for include values and other one for exclude values
 * In case we try to add more than two filters, sage would combine them.
 * eg: If IN and NOT_IN filters are already added, we try to add one more filter
 * using EQ operator, it would be combined with IN filter.
 *
 * Another assumption is that there would be separate filters for different
 * operators. eg: Filters with operator IN and NOT_IN will have two different
 * filters with different filterIds
 * @param filters
 */

const filterTypesEditableByWidget = [
    FilterTypes.Eq,
    FilterTypes.In,
    FilterTypes.Ne,
];

export enum TabHeader {
    Include = 'INCLUDE',
    Exclude = 'EXCLUDE',
}

export enum PLACEMENT {
    LEFT = 'LEFT',
    RIGHT = 'RIGHT',
}

export type AttributeFilterWarning = {
    message: string;
    primaryLink?: AlertLink;
    suppressOnClose?: boolean;
};

export function getAttributeFilterInput(filters: Filter[]) {
    let includeFilter: Filter;
    let excludeFilter: Filter;
    _.forEach(filters, filterFragment => {
        _.forEach(filterFragment?.filterContent, content => {
            // we need to neglect unsupported filterTypes because we don't support them on filter widget
            if (!filterTypesEditableByWidget.includes(content.filterType)) {
                return;
            }
            if (
                [FilterTypes.Eq, FilterTypes.In].includes(content.filterType) &&
                !content.negate
            ) {
                includeFilter = filterFragment;
            } else {
                excludeFilter = filterFragment;
            }
        });
    });
    return { includeFilter, excludeFilter };
}

export function getValueListFromFilter(filter: Filter): string[] {
    if (filter && !_.isEmpty(filter.filterContent)) {
        return _.reduce(
            filter?.filterContent,
            (acc: string[], filterContent: FilterContent) => {
                const filterValue: string[] = filterContent?.value.map(
                    getAttributeFilterValue,
                );
                return acc.concat(filterValue);
            },
            [],
        );
    }
    return [];
}

export function shouldApplyFilter(
    existingFilter: Filter,
    newValues: string[],
): boolean {
    // should not apply new filter if newValues list is empty
    if (_.isEmpty(newValues)) return false;
    const existingValues = getValueListFromFilter(existingFilter);
    // should not apply filter is existing values is same as new values
    if (_.isEmpty(_.xor(existingValues, newValues))) return false;

    return true;
}

export function checkIfValueChanged(
    existingFilter: Filter,
    newValues: string[],
): boolean {
    const existingValues = getValueListFromFilter(existingFilter);
    // should not apply filter if existing values is same as new values
    if (_.isEmpty(_.xor(existingValues, newValues))) return false;

    return true;
}

export function checkIfAttributeValueSimilar(
    includeFilter: Filter,
    includeValues: string[],
    excludeFilter: Filter,
    excludeValues: string[],
): boolean {
    // should not apply filter if existing values is same as new values
    if (
        !checkIfValueChanged(includeFilter, includeValues) &&
        !checkIfValueChanged(excludeFilter, excludeValues)
    )
        return true;

    return false;
}

function splitWithEscapes(input: string): string[] {
    // Define delimiters
    const delimiters: string[] = [',', ';', '\n', '\t'];
    const result: string[] = [];
    let current = '';
    let escaped = false;

    input.split('').forEach(char => {
        if (escaped) {
            current += char; // Append the escaped character directly
            escaped = false;
        } else if (char === '\\') {
            escaped = true; // Start escape sequence
        } else if (delimiters.includes(char)) {
            if (current.length > 0) {
                result.push(_.trim(current)); // Push accumulated value
                current = '';
            }
            // Delimiters are used to split the string, not included in results
        } else {
            current += char; // Append regular character
        }
    });

    // Push any remaining content after the loop
    if (current.length > 0) {
        result.push(_.trim(current));
    }

    return result;
}

export function getBulkFilterValuesList(
    bulkFilterValuesString: string,
    existingIncludeFilterValues: string[] | null,
) {
    const result = splitWithEscapes(bulkFilterValuesString)
        .map(val => getTextFromHTMLTags(val))
        .filter(Boolean);
    return _.isEmpty(existingIncludeFilterValues)
        ? result
        : _.uniq(result.concat(existingIncludeFilterValues));
}

const CAPTION_REGEX = /{caption}(.*){\/caption}(.*)/g;

/**
 * Returns if filter value matches caption regex.
 * e.g. {caption}Some caption{/caption}http://www.google.com
 */
export const isCaptioned = (value: any) =>
    _.isString(value) && !!value.match(CAPTION_REGEX);

export function highlightSubstringWithStrong(
    textValue: string,
    pattern: string,
) {
    // Regex to find all occurrences of the substring in the string
    const escapedPattern = _.escapeRegExp(pattern);
    const regex = new RegExp(escapedPattern, 'gi');
    // Replace the substrings with the same substring wrapped in a <strong> tag
    const highlightedStr = textValue.replace(
        regex,
        match =>
            `<strong data-testid="highlighted-pattern-${pattern}">${match}</strong>`,
    );
    return highlightedStr;
}

/**
 * Converts captioned filter value to HTML for display
 * e.g. {caption}Some caption{/caption}http://www.google.com
 * returns <a href="http://www.google.com">Some caption</a>
 */
const getCaptionHtmlWithNoHighlights = (
    value: string,
    target: LINK_TARGET = LINK_TARGET.BLANK,
) => {
    const { caption, link } = extractCaptionTitleAndLink(value);
    // eslint-disable-next-line thoughtspot-eslint/restrict-anchor-tags -- [SCAL-278207] Static links don't need customization [TSE-Reviewed]
    return `<a href="${encodeURI(link)}" class="${
        styles.caption
    }" target="${target}" rel="noopener">${caption}</a>`;
};

/**
 * Wraps html filter value within SafeHTML
 */
const getFilterValueLabelWithNoHighlights = (
    value: string,
    cellLinkTarget: LINK_TARGET,
) => {
    const htmlString = isCaptioned(value)
        ? getCaptionHtmlWithNoHighlights(value, cellLinkTarget)
        : value;
    return (
        <SafeHTML
            html={htmlString}
            config={{ ADD_ATTR: ['target'] }}
            hyperLinkSanitization
        />
    );
};

/**
 * Converts captioned filter value to HTML for display with bold highlighted text
 * e.g. {caption}Some caption{/caption}http://www.google.com with highlightedPattern caption
 * returns <a href="http://www.google.com">Some <strong>caption</strong></a>
 */
const getCaptionHtmlWithHighlights = (
    value: string,
    cellLinkTarget: LINK_TARGET,
    highlightedPattern: string,
) => {
    const { caption, link } = extractCaptionTitleAndLink(value);
    const captionWithHighlightedText = highlightSubstringWithStrong(
        caption,
        highlightedPattern,
    );
    // eslint-disable-next-line thoughtspot-eslint/restrict-anchor-tags -- [SCAL-278207] Static links don't need customization [TSE-Reviewed]
    return `<a href="${encodeURI(link)}" class="${
        styles.caption
    }" target="${cellLinkTarget}" rel="noopener">${captionWithHighlightedText}</a>`;
};

/**
 * Adds bold highlightedPattern in the html filter value
 */
export const replaceHighlightedTextWithStrong = (
    html: string,
    highlightedPattern: string,
): string => {
    const $ = cheerio.load(html);

    $(`*:contains(${highlightedPattern})`)
        .contents()
        .filter((index, element: any) => {
            return (
                element.nodeType === Node.TEXT_NODE &&
                element.nodeValue &&
                element.nodeValue.includes(highlightedPattern)
            );
        })
        .each((index, element: any) => {
            const replacedNode = element.nodeValue.replaceAll(
                highlightedPattern,
                `<strong>${highlightedPattern}</strong>`,
            );
            $(element).replaceWith(replacedNode);
        });

    return $('body').html();
};

/**
 * Wraps html filter value within SafeHTML with bold highlighted text
 */
const getFilterValueLabelWithHighlights = (
    value: string,
    cellLinkTarget: LINK_TARGET,
    highlightedPattern: string,
) => {
    const htmlString = isCaptioned(value)
        ? getCaptionHtmlWithHighlights(
              value,
              cellLinkTarget,
              highlightedPattern,
          )
        : replaceHighlightedTextWithStrong(value, highlightedPattern);
    return (
        <SafeHTML
            html={htmlString}
            config={{ ADD_ATTR: ['target'] }}
            hyperLinkSanitization
        />
    );
};

/**
 * Strips HTML tags from filter values
 */
export const getFilterValue = (labelValue: string, defaultText = '') => {
    if (labelValue === null) return labelValue;
    return (
        labelValue
            // for handling HTML in compare tab suggestions
            .split(' vs ')
            .map(label =>
                isCaptioned(label)
                    ? extractCaptionTitleAndLink(label).caption
                    : getTextFromHTMLTags(label, defaultText),
            )
            .join(' vs ')
    );
};

/**
 * wrap parts of the label string that match highlightedPattern in strong tag
 * @param label
 * @param highlightedPattern
 */
export function getHighlightedLabel(
    label: string,
    highlightedPattern: string,
    isHtmlRenderingDisabled: boolean,
    cellLinkTarget: LINK_TARGET = LINK_TARGET.BLANK,
) {
    const filterValue = getFilterValue(label);
    const shouldRenderHtml =
        !isHtmlRenderingDisabled && (isCaptioned(label) || hasHtmlTags(label));
    const patternLowerCase = highlightedPattern?.toLowerCase();

    if (
        _.isEmpty(highlightedPattern) ||
        !filterValue.toLowerCase().includes(patternLowerCase)
    ) {
        if (!shouldRenderHtml) {
            return filterValue;
        }
        return getFilterValueLabelWithNoHighlights(label, cellLinkTarget);
    }

    if (!shouldRenderHtml) {
        // special characters in regex need to be escaped so as to prevent errors.
        const escapedHighlightedPattern = _.escapeRegExp(highlightedPattern);
        const highlightRegex = new RegExp(
            `(${escapedHighlightedPattern})`,
            'gi',
        );
        const labelList = filterValue.split(highlightRegex);
        return labelList.map((subLabel, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
                {subLabel.toLowerCase() === highlightedPattern.toLowerCase() ? (
                    <strong data-testid={`highlighted-pattern-${subLabel}`}>
                        {subLabel}
                    </strong>
                ) : (
                    subLabel
                )}
            </React.Fragment>
        ));
    }

    return getFilterValueLabelWithHighlights(
        label,
        cellLinkTarget,
        highlightedPattern,
    );
}

/**
 * Returns if the searched value in filter modal is a part of HTML tags
 * and not inner text
 */
export const isHTMLSearchedFilterValue = (
    label: string,
    highlightedPattern: string,
) => {
    if (label === null) return false;
    const filterValueWithoutHTMLTags = isCaptioned(label)
        ? extractCaptionTitleAndLink(label).caption
        : getTextFromHTMLTags(label);
    if (
        label.includes(highlightedPattern) &&
        !filterValueWithoutHTMLTags.includes(highlightedPattern)
    ) {
        return true;
    }
    return false;
};

export function getUniqueValueList(
    tabValueList: string[],
    bulkFilterValueList: string[],
    prevStateOfValueList: string[],
) {
    return _.uniqBy(
        [
            ...tabValueList.sort(alphaNumericCompareFn),
            ...bulkFilterValueList,
            ...prevStateOfValueList,
        ],
        _.toLower,
    );
}

export function canEditAttributeFilterUsingWidget(filterGroup: FilterGroup) {
    const { filters, columnInfo } = filterGroup;
    if (isFilterNonEditable(filterGroup)) {
        return false;
    }
    // In filterValues API call we are getting names like Monday, Tuesday or January, February or q1, q2
    // but expectation here is to get numbers like 1, 2, 3 etc if user applies any of the below bucket
    // Day of week: we are expecting Monday, Tuesday... but getting 1,2,3...
    // Month of year: we are expecting January, February... but getting 1,2,3...
    // Quarter of year: we are expecting q1, q2... but getting 1,2,3...
    // We are blocking the Modal whenever we have any of these bucketing until this is not
    // fixed by Sage.
    if (
        [
            ColumnTimeBucket.DayOfWeek,
            ColumnTimeBucket.MonthOfYear,
            ColumnTimeBucket.QuarterOfYear,
        ].includes(columnInfo?.timeBucket)
    ) {
        return false;
    }
    // This is needed when zero filters applied
    if (!filters || !filters.length) {
        if (NUMERICAL_DATE_BUCKETS.includes(columnInfo?.timeBucket)) {
            return false;
        }
        return true;
    }
    const canEditFilterUsingWidget = filters?.some(filter => {
        if (!_.isEmpty(filter.filterContent)) {
            return filter.filterContent?.some(
                (filterContent: FilterContent) => {
                    return filterTypesEditableByWidget.includes(
                        filterContent.filterType,
                    );
                },
            );
        }
        logger.error(
            'unexpected behaviour filter content should not be null/empty',
        );
        return false;
    });
    return canEditFilterUsingWidget;
}

export const getIncludeExludeHeaderType = (
    includeFilter?: Filter,
    excludeFilter?: Filter,
    attributeFilter?: AttributeFilter,
) => {
    const includedValues = getValueListFromFilter(includeFilter);
    const excludedValues = getValueListFromFilter(excludeFilter);
    if (
        (!attributeFilter?.includeValues &&
            !attributeFilter?.excludeValues &&
            includedValues.length === 0 &&
            excludedValues.length !== 0) ||
        (_.isEmpty(attributeFilter?.includeValues) &&
            !_.isEmpty(attributeFilter?.excludeValues))
    ) {
        return TabHeader.Exclude;
    }
    return TabHeader.Include;
};

export const stringToFilterColumnValueInput = (
    val: string,
): FilterColumnValueInput => {
    const filterColumnValueInput: FilterColumnValueInput = {
        type: ColumnValueType.Char,
    };
    if (
        _.isNil(val) ||
        val?.toLowerCase() ===
            translate('NULL_VALUE_PLACEHOLDER_LABEL', {
                skipInterpolation: true,
            }).toLowerCase()
    ) {
        filterColumnValueInput.isNull = true;
    } else if (_.isEmpty(val.trim())) {
        filterColumnValueInput.stringValue = translate(
            'EMPTY_VALUE_PLACEHOLDER_LABEL',
            {
                skipInterpolation: true,
            },
        );
    } else {
        filterColumnValueInput.stringValue = val;
    }
    return filterColumnValueInput;
};

export const getApplicableAttributeFilters = (
    includeFilter: Filter,
    excludeFilter: Filter,
    selectedAttributeFilter: AttributeFilter,
) => {
    const filterContent: FilterContentInput[] = [];
    const removeFilterIds: string[] = [];
    const currIncludeFilterValueList = selectedAttributeFilter?.includeValues;
    const currExcludeFilterValueList = selectedAttributeFilter?.excludeValues;

    // only apply filter if there is some change
    if (shouldApplyFilter(includeFilter, currIncludeFilterValueList)) {
        const includeFilterContent: FilterContentInput = {
            filterType: FilterTypes.In,
            value: currIncludeFilterValueList.map(
                stringToFilterColumnValueInput,
            ),
        };
        if (includeFilter?.filterId)
            includeFilterContent.filterId = includeFilter.filterId;
        filterContent.push(includeFilterContent);
    }
    if (shouldApplyFilter(excludeFilter, currExcludeFilterValueList)) {
        const excludeFilterContent: FilterContentInput = {
            filterType: FilterTypes.In,
            value: currExcludeFilterValueList.map(
                stringToFilterColumnValueInput,
            ),
            negate: true,
        };
        if (excludeFilter?.filterId)
            excludeFilterContent.filterId = excludeFilter.filterId;
        filterContent.push(excludeFilterContent);
    }

    if (includeFilter && _.isEmpty(currIncludeFilterValueList))
        removeFilterIds.push(includeFilter.filterId);
    if (excludeFilter && _.isEmpty(currExcludeFilterValueList))
        removeFilterIds.push(excludeFilter.filterId);

    // add empty filter if we are removing all the filters
    if (
        _.isEmpty(currIncludeFilterValueList) &&
        _.isEmpty(currExcludeFilterValueList)
    ) {
        filterContent.push({
            filterType: FilterTypes.In,
            value: [],
        });
    }

    return {
        filterContent,
        removeFilterIds,
    };
};

/**
 * Ensures single select filters only contain one value by taking the last selected value
 * @param updatedFilter - The incoming filter update
 * @returns AttributeFilter with only one value in includeValues
 */
export const ensureSingleSelectValue = (
    updatedFilter: AttributeFilter,
): AttributeFilter => {
    const updatedFilterValues = { ...updatedFilter }?.includeValues;
    const includeValues =
        updatedFilterValues && updatedFilterValues.length > 0
            ? [updatedFilterValues[updatedFilterValues.length - 1]]
            : [];

    return {
        includeValues,
        excludeValues: [],
    };
};
